import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField, Heading, FieldCheckboxGroup } from '../../../components';
import { requiredFieldArrayCheckbox } from '../../../util/validators';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './ConfirmSignupForm.module.css';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const ConfirmSignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        authInfo,
        idp,
        preselectedUserType,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      if (typeof localStorage !== 'undefined') {
        const sanitize = v => (v && v !== 'undefined' ? v : null);
        values.userType = sanitize(localStorage.getItem('selectUserType')) || preselectedUserType || null;
        values.role = sanitize(localStorage.getItem('inviteRole'));
        values.profileListingId = sanitize(localStorage.getItem('inviteListingId'));
      }

      let { userType } = values || {};

      if (!userType) {
        values.userType = 'candidate';
        userType = 'candidate';
      }

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailInvalid',
        })
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      // const showDefaultUserFields = true;
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName } = authInfo;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.formContainerDiv}>
            {/* <FieldSelectUserType
              name="userType"
              userTypes={userTypes}
              hasExistingUserType={!!preselectedUserType}
              intl={intl}
            /> */}

            <Heading as="h1" rootClassName={css.signupWithIdpTitle}>
              {values?.userType === 'agency'
                ? 'Supplier Signup'
                : values?.userType === 'customer'
                  ? 'Customer Signup'
                  : 'Candidate Signup'}<FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitleConfirmPage" values={{ idp: idp === 'Microsoftproxy' ? 'Microsoft' : idp === 'Microsoftproxysso' ? 'Microsoft' : idp?.toLowerCase()?.includes('microsoft') ? 'Microsoft' : idp }} />
            </Heading>

            {/* <p className={css.confirmInfoText}>
              <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
            </p> */}

            {/* <div className={css.signUpHeading}>
              <h1 className={css.signUpHeading}>
                {values?.userType === 'agency'
                  ? 'Agency Signup'
                  : values?.userType === 'customer'
                  ? 'Customer Signup'
                  : 'Candidate Signup'}
              </h1>
            </div> */}

            {showDefaultUserFields ? (
              <div className={css.defaultUserFields}>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({
                    id: 'ConfirmSignupForm.emailLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.emailPlaceholder',
                  })}
                  initialValue={email}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  disabled={true}
                />
                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id={formId ? `${formId}.firstName` : 'firstName'}
                    name="firstName"
                    autoComplete="given-name"
                    label={intl.formatMessage({
                      id: 'ConfirmSignupForm.firstNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'ConfirmSignupForm.firstNamePlaceholder',
                    })}
                    initialValue={firstName}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'ConfirmSignupForm.firstNameRequired',
                      })
                    )}
                    disabled={true}
                  />
                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.lastName` : 'lastName'}
                    name="lastName"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'ConfirmSignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'ConfirmSignupForm.lastNamePlaceholder',
                    })}
                    initialValue={lastName}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'ConfirmSignupForm.lastNameRequired',
                      })
                    )}
                    disabled={true}
                  />
                </div>

                <UserFieldDisplayName
                  formName="ConfirmSignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                  disabled={true}
                />
                <UserFieldPhoneNumber
                  formName="ConfirmSignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                  disabled={true}
                />
              </div>
            ) : null}

            {showCustomUserFields ? (
              <div className={css.customFields}>
                {userFieldProps.map(({ key, ...fieldProps }) => (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                ))}
              </div>
            ) : null}

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <FieldCheckboxGroup
                name="userTypeConfirmed"
                id={formId ? `${formId}.userTypeConfirmed` : 'userTypeConfirmed'}
                className={css.userTypeConfirmRoot}
                optionLabelClassName={css.userTypeConfirmLabel}
                options={[
                  {
                    key: 'confirmed',
                    label: `I understand that I am signing up as a ${userType === 'agency' ? 'Supplier' : userType === 'customer' ? 'Customer' : 'Candidate'} and that I cannot use the same email to sign up with a different user type.`,
                  },
                ]}
                validate={requiredFieldArrayCheckbox('Please confirm your account type to continue.')}
              />
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                className={css.submitButton}
              >
                {submitInProgress ? (
                  <FormattedMessage id="ConfirmSignupForm.submitting" />
                ) : (
                  <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp === 'Microsoftproxy' ? 'Microsoft' : idp }} />
                )}
              </PrimaryButton>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the confirm signup form, which is used with SSO authentication.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const ConfirmSignupForm = props => {
  const intl = useIntl();
  return <ConfirmSignupFormComponent {...props} intl={intl} />;
};

export default ConfirmSignupForm;
